import { useCallback } from 'react';
import { deepCloneNestedSprintTree } from '../../../FlowchartView/hooks/clipboardUtils/directDuplicate';

/**
 * useDuplicateTask Hook
 * 
 * Duplicates a task (used from Table view). For nested sprints, deep-clones
 * the entire sprint with full ID remapping via deepCloneNestedSprintDirect
 * — the same proven pattern used by templateCopy.js.
 */
export const useDuplicateTask = ({
  tasks,
  nestedSprints,
  localSprint,
  onAddSprint,
  onUpdateSprint,
  setLocalSprint,
}) => {
  const handleDuplicateTaskWithNested = useCallback((taskId) => {
    const taskToDuplicate = tasks.find(t => t.id === taskId);
    if (!taskToDuplicate) return;
    
    const now = new Date().toISOString();
    const newTaskId = `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const duplicatedTask = {
      ...JSON.parse(JSON.stringify(taskToDuplicate)),
      id: newTaskId,
      name: `${taskToDuplicate.name} (copy)`,
      order: tasks.length + 1,
      status: 'pending',
      completedAt: null,
      blockedReason: null,
      blockedAt: null,
      createdAt: now,
      updatedAt: now,
      startedAt: null,
      dueDate: null,
      dependsOn: [],
      unlockConditions: [],
      subtasks: (taskToDuplicate.subtasks || []).map((st, idx) => ({
        ...st,
        id: `subtask-${Date.now()}-${idx}`,
      })),
      deliverables: (taskToDuplicate.deliverables || []).map((del, idx) => ({
        ...del,
        id: `deliverable-${Date.now()}-${idx}`,
        completed: false,
        link: null,
      })),
      inputs: (taskToDuplicate.inputs || []).map((inp, idx) => ({
        ...inp,
        id: `input-${Date.now()}-${idx}`,
      })),
      ...(taskToDuplicate.x !== undefined && { x: taskToDuplicate.x + 40 }),
      ...(taskToDuplicate.y !== undefined && { y: taskToDuplicate.y + 40 }),
    };
    
    if (duplicatedTask.nodeType === 'decision' && duplicatedTask.options) {
      duplicatedTask.options = duplicatedTask.options.map((opt, idx) => ({
        ...opt,
        id: `option-${Date.now()}-${idx}`,
      }));
      duplicatedTask.answer = null;
    }
    
    let clonedNestedRoot = null;
    let clonedNestedDescendants = [];

    if (taskToDuplicate.nestedSprintId) {
      const originalNested = nestedSprints.find(ns => ns.id === taskToDuplicate.nestedSprintId);
      if (originalNested && onAddSprint) {
        const { sprint: clonedNested, descendants } = deepCloneNestedSprintTree(
          originalNested,
          newTaskId,
          localSprint?.id,
          nestedSprints,
          [],
          null,
        );
        if (clonedNested) {
          duplicatedTask.nestedSprintId = clonedNested.id;
          clonedNestedRoot = clonedNested;
          clonedNestedDescendants = descendants || [];
        }
      } else {
        duplicatedTask.nestedSprintId = undefined;
      }
    }

    const updatedSprint = {
      ...(localSprint || {}),
      tasks: [...(localSprint?.tasks || tasks || []), duplicatedTask],
    };

    setLocalSprint(prev => {
      if (!prev) return prev;
      return { ...prev, tasks: [...(prev.tasks || []), duplicatedTask] };
    });

    if (onUpdateSprint) {
      onUpdateSprint(updatedSprint);
    }

    if (clonedNestedRoot && onAddSprint) {
      onAddSprint({ ...clonedNestedRoot, parentSprintId: clonedNestedRoot.parentSprintId ?? localSprint?.id });
      clonedNestedDescendants.forEach((child) => onAddSprint(child));
    }
    
    console.log(`📋 Duplicated "${taskToDuplicate.name}" with nested sprints`);
  }, [tasks, nestedSprints, localSprint, onAddSprint, onUpdateSprint, setLocalSprint]);

  return { handleDuplicateTaskWithNested };
};

export default useDuplicateTask;
